import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArchiveRestore,
  Trash2,
  StickyNote,
  CheckSquare,
  Folder as FolderIcon,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useListArchive,
  useRestoreArchiveItem,
  useDeleteNote,
  useDeleteTask,
  useDeleteFolder,
  type ArchiveItem,
} from "@workspace/api-client-react";

const TYPE_ICON: Record<ArchiveItem["type"], typeof StickyNote> = {
  note: StickyNote,
  task: CheckSquare,
  folder: FolderIcon,
};

const TYPE_LABEL: Record<ArchiveItem["type"], string> = {
  note: "Note",
  task: "Task",
  folder: "Folder",
};

export function ArchivePage() {
  const queryClient = useQueryClient();
  const { data: items = [], isLoading } = useListArchive();
  const restoreItem = useRestoreArchiveItem();
  const deleteNote = useDeleteNote();
  const deleteTask = useDeleteTask();
  const deleteFolder = useDeleteFolder();

  const sorted = [...items].sort(
    (a, b) => new Date(b.archivedAt).getTime() - new Date(a.archivedAt).getTime(),
  );

  const invalidateAfterChange = () => {
    queryClient.invalidateQueries();
  };

  const handleRestore = (item: ArchiveItem) => {
    restoreItem.mutate(
      { type: item.type, id: item.id },
      { onSuccess: invalidateAfterChange },
    );
  };

  const handleMoveToTrash = (item: ArchiveItem) => {
    const confirmed = window.confirm(
      "Move this " + TYPE_LABEL[item.type].toLowerCase() + " to trash?",
    );
    if (!confirmed) return;

    if (item.type === "note") {
      deleteNote.mutate({ id: item.id }, { onSuccess: invalidateAfterChange });
    } else if (item.type === "task") {
      deleteTask.mutate({ id: item.id }, { onSuccess: invalidateAfterChange });
    } else {
      deleteFolder.mutate({ id: item.id }, { onSuccess: invalidateAfterChange });
    }
  };

  return (
    <div className="max-w-4xl mx-auto h-[calc(100vh-4rem)] flex flex-col px-4">
      <div className="pt-4 pb-2 shrink-0">
        <p className="text-sm text-muted-foreground">
          Archived items are hidden from your normal views but kept safe here.
        </p>
      </div>

      <ScrollArea className="flex-1 -mx-4 px-4 pb-6">
        <div className="flex flex-col gap-3 py-2">
          {isLoading ? (
            <div className="text-center text-muted-foreground py-10">
              Loading archive...
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center text-muted-foreground py-20">
              Archive is empty.
            </div>
          ) : (
            sorted.map((item) => {
              const Icon = TYPE_ICON[item.type];
              return (
                <div
                  key={item.type + "-" + item.id}
                  className="bg-card border rounded-2xl px-4 py-3.5 shadow-sm flex items-center gap-3"
                >
                  <div className="h-9 w-9 shrink-0 rounded-full bg-muted flex items-center justify-center">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-medium truncate">
                      {item.title}
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {TYPE_LABEL[item.type]}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRestore(item)}
                    className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-primary"
                    aria-label="Restore"
                  >
                    <ArchiveRestore className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveToTrash(item)}
                    className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-red-500"
                    aria-label="Move to trash"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
